import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Upload, Shield, Monitor, Radio, ChevronRight, LogOut, User, DollarSign, UserCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_CARDS = [
  { role: 'brand', title: 'Brand Dashboard', desc: 'Upload ads, browse streamers, create campaigns and place bids.', icon: Upload, path: '/brand', color: '#7C3AED' },
  { role: 'admin', title: 'Admin Panel', desc: 'Review ads & placements, manage CPM tiers, process payouts.', icon: Shield, path: '/admin', color: '#FF3B30' },
  { role: 'streamer', title: 'Streamer Console', desc: 'Trigger ads live, manage your ad queue, control OBS overlay.', icon: Monitor, path: '/streamer', color: '#10B981' },
  { role: 'streamer', title: 'Streamer Profile', desc: 'Set up your streaming stats for brands to discover you.', icon: UserCircle, path: '/streamer/profile', color: '#3B82F6' },
  { role: 'streamer', title: 'Earnings', desc: 'Track your revenue, view payout history, request withdrawals.', icon: DollarSign, path: '/streamer/earnings', color: '#F59E0B' },
];

export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const obsUrl = `${window.location.origin}/obs-overlay`;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center">
              <Radio className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-sm normal-case">source</span></span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="user-menu-button" variant="ghost" className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5">
                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><User className="w-3.5 h-3.5" /></div>
                <span className="text-sm font-medium">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-[#121212] border-white/10">
              <DropdownMenuLabel className="text-gray-400">
                <p className="text-sm text-white font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500 font-mono">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="logout-button" className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-12 animate-fade-in-up">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white uppercase mb-3">Control Center</h1>
          <p className="text-gray-500 text-base max-w-xl">Source-integrated ad network for live streams. Select a console to get started.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {user?.roles?.map((role) => (
            <Badge key={role} className="px-2 py-1 rounded text-xs font-bold uppercase tracking-wider font-mono bg-white/5 text-gray-300 border border-white/10">{role}</Badge>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {ROLE_CARDS.map((card, i) => {
            const hasRole = user?.roles?.includes(card.role);
            const Icon = card.icon;
            return (
              <button key={`${card.role}-${card.path}`} data-testid={`nav-${card.path.replace(/\//g, '-').slice(1)}`}
                disabled={!hasRole} onClick={() => navigate(card.path)}
                className={`group text-left p-6 border rounded-lg transition-all duration-300 ${hasRole ? 'border-white/10 bg-[#121212]/50 hover:border-white/20 hover:bg-[#1E1E1E]/60 cursor-pointer' : 'border-white/5 bg-[#0a0a0a] opacity-40 cursor-not-allowed'}`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${card.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: card.color }} />
                  </div>
                  {hasRole && <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-300 transition-colors" />}
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
                {!hasRole && <p className="text-xs font-mono text-gray-600 mt-3 uppercase tracking-wider">Role required</p>}
              </button>
            );
          })}
        </div>

        <div className="mt-12 p-6 border border-white/10 rounded-lg bg-[#121212]/50">
          <h3 className="font-heading text-lg font-bold text-white mb-3">OBS Browser Source</h3>
          <p className="text-sm text-gray-400 mb-4">Add this URL as a Browser Source in OBS to display ads during your stream.</p>
          <div className="flex items-center gap-3">
            <code data-testid="obs-overlay-url" className="flex-1 bg-black/40 border border-white/10 rounded px-4 py-3 text-sm font-mono text-emerald-400 select-all">{obsUrl}</code>
            <Button data-testid="copy-obs-url-button" variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/5 border border-white/10 px-4"
              onClick={() => { navigator.clipboard.writeText(obsUrl); toast.success('URL copied'); }}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
